import { createTheme } from '@mui/material/styles';

/**
 * Единые пресеты тем для админки.
 * Можно переключать через localStorage ключ `muiThemePreset`.
 * Доступные значения: `adminDark` | `muiDefaultDark` | `muiDashboardV7`.
 */
const adminDarkThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#5F94E2',
      contrastText: '#fff',
    },
    secondary: {
      main: 'rgba(255, 255, 255, 0.6)',
    },
    error: {
      main: '#EB5B47',
    },
    warning: {
      main: '#ed6c02',
      dark: '#e65100',
    },
    success: {
      main: '#2e7d32',
      dark: '#1b5e20',
    },
    background: {
      default: '#121212',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.87)',
      secondary: 'rgba(255, 255, 255, 0.6)',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Arial", sans-serif',
    h5: {
      fontWeight: 500,
    },
    body1: {
      color: 'rgba(255, 255, 255, 0.87)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          color: 'rgba(255, 255, 255, 0.87)',
        },
        head: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          color: 'rgba(255, 255, 255, 0.87)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backgroundImage: 'none',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.87)',
        },
        input: {
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.5)',
            opacity: 1,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': { borderBottomColor: 'rgba(255, 255, 255, 0.12)' },
          '&:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(255, 255, 255, 0.3)' },
          '&:after': { borderBottomColor: '#5F94E2' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: 'rgba(255, 255, 255, 0.12)',
        },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#5F94E2',
            borderWidth: 1,
          },
        },
      },
    },
  },
};

const muiDefaultDarkThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#9c27b0',
    },
    warning: {
      main: '#ed6c02',
      dark: '#e65100',
    },
    success: {
      main: '#2e7d32',
      dark: '#1b5e20',
    },
    background: {
      default: '#121212',
      paper: '#1d1d1d',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'none',
    },
  },
};

// Базовый светлый пресет, визуально близкий к бесплатному Dashboard template MUI.
const muiDashboardThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      contrastText: '#fff',
    },
    secondary: {
      main: '#9c27b0',
    },
    error: {
      main: '#d32f2f',
    },
    warning: {
      main: '#ed6c02',
      dark: '#e65100',
    },
    success: {
      main: '#2e7d32',
      dark: '#1b5e20',
    },
    background: {
      default: '#f6f8fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#111827',
      secondary: '#6b7280',
    },
    divider: 'rgba(17, 24, 39, 0.12)',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    h5: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
      variants: [
        {
          props: { elevation: 1 },
          style: {
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
          },
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(17, 24, 39, 0.08)',
        },
        head: {
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
  },
};

const themePresets = {
  adminDark: adminDarkThemeOptions,
  muiDefaultDark: muiDefaultDarkThemeOptions,
  muiDashboard: muiDashboardThemeOptions,
  muiDashboardV7: muiDashboardThemeOptions,
};

export const buildMuiTheme = (presetName = 'adminDark') => {
  const selected = themePresets[presetName] || themePresets.adminDark;
  return createTheme(selected);
};

export const muiTheme = buildMuiTheme('adminDark');

export default muiTheme;
